/* STATE */
import { push } from 'connected-react-router';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { AppAction, createActionType, createApiActionCreators, RequestActionTypes } from 'helpers/redux/reduxHelpers';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectFinancialDocumentInvoiceNumExt } from 'saga/billing/detail/ducks';
import { selectBillingCaRefNo } from 'saga/billing/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import {
  selectClaimId,
  selectInvoiceNumber,
  setClaimIdOldAction,
  setCurrentStepAction,
  setInvoiceNumberOldAction,
} from 'saga/billingManagement/claims/ducks';
import { AddNextClaimResponseVO } from 'generated/bslEbill/claims/claims6/models';

export interface ClaimStep6Payload {
  invoiceNumber?: string;
  claimId?: number;
  serviceId?: number;
  internalRemark?: string;
  caRefNo?: string;
  contactEmail?: string;
  noEmailFlag?: boolean;
  assignedTo?: string;
  accountNo?: string;
  removeClaim?: boolean;
}

/* ACTION TYPES */
export enum ClaimStep6StateTypes {
  GetData = '@@ClaimStep6/GET_DATA',
  AddNextClaim = '@@ClaimStep6/SAVE_CLAIM_STEP_6',
  AssignClaim = '@@ClaimStep6/ASSIGN_CLAIM',
  CancelClaimByParent = '@@ClaimStep6/CANCEL_CLAIM_BY_PARENT',
  ProcessClaim = '@@ClaimStep6/PROCESS_CLAIM',
  SendClaim = '@@ClaimStep6/SEND_CLAIM',
}

/* ACTIONS */
export const getClaimStep6DataActions = createApiActionCreators(ClaimStep6StateTypes.GetData);
export const addNextClaimActions = createApiActionCreators(ClaimStep6StateTypes.AddNextClaim);
export const assignClaimActions = createApiActionCreators(ClaimStep6StateTypes.AssignClaim);
export const cancelClaimByParentActions = createApiActionCreators(ClaimStep6StateTypes.CancelClaimByParent);
export const processClaimActions = createApiActionCreators(ClaimStep6StateTypes.ProcessClaim);
export const sendClaimActions = createApiActionCreators(ClaimStep6StateTypes.SendClaim);

/* SAGAS */

function* getData({ payload }: AppAction<ClaimStep6Payload>) {
  if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.getData,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.accountNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getClaimStep6DataActions.success(resp.data));
  } else {
    yield put(getClaimStep6DataActions.failure());
  }
}

function* assignClaim({ payload }: AppAction<ClaimStep6Payload>) {
  if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.assignClaim,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.internalRemark,
      payload.contactEmail,
      payload.noEmailFlag,
      payload.assignedTo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(assignClaimActions.success());
  } else {
    yield put(assignClaimActions.failure());
  }
}

function* processClaim({ payload }: AppAction<ClaimStep6Payload>) {
  if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.processClaim,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.internalRemark,
      payload.contactEmail,
      payload.noEmailFlag,
      payload.assignedTo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(processClaimActions.success());
  } else {
    yield put(processClaimActions.failure());
  }
}

function* sendClaim({ payload }: AppAction<ClaimStep6Payload>) {
  if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.sendClaim,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.internalRemark,
      payload.contactEmail,
      payload.noEmailFlag,
      payload.assignedTo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(sendClaimActions.success());
  } else {
    yield put(sendClaimActions.failure());
  }
}

function* addNextClaim({ payload }: AppAction<ClaimStep6Payload>) {
  const invoiceNumber: string = yield select(selectInvoiceNumber);
  const claimId: number = yield select(selectClaimId);

  if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.addNextClaim,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.internalRemark,
      payload.contactEmail,
      payload.noEmailFlag,
      payload.assignedTo
    ),
  };
  const resp: ExtendedAxiosResponse<AddNextClaimResponseVO> = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(addNextClaimActions.success());
    yield put(setInvoiceNumberOldAction(invoiceNumber));
    yield put(setClaimIdOldAction(claimId));
    yield put(
      setCurrentStepAction({
        step: 1,
        invoiceNumber: resp?.data?.newClaim?.invoiceNo,
        claimId: resp?.data?.newClaim?.id,
        serviceId: resp?.data?.newClaim?.claimServiceId,
        resetWizard: true,
      })
    );
  } else {
    yield put(addNextClaimActions.failure());
  }
}

function* cancelClaimByParent({ payload }: AppAction<ClaimStep6Payload>) {
  if (!payload.invoiceNumber || !payload.claimId) return;

  const caRefNo: string | null | undefined = yield select(selectBillingCaRefNo);
  const invoiceNumExt: string = yield select(selectFinancialDocumentInvoiceNumExt);
  const request: ApiClientRequest = {
    apiCall: call(
      api.cancelClaimByParent,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(cancelClaimByParentActions.success());

    if (resp?.data?.kahl) {
      yield put(getKahlActions.request({ infoMessageIds: resp.data.kahl }));
    }

    yield put(
      push(
        `${buildRoute(ROUTES.BillingDetail, {
          page: 'month-statement-detail',
        })}?invoiceNumExt=${invoiceNumExt}&billingCaRefNo=${payload.caRefNo || caRefNo || ''}`
      )
    );
  } else {
    yield put(cancelClaimByParentActions.failure());
    yield put(
      push(
        `${buildRoute(ROUTES.BillingDetail, {
          page: 'month-statement-detail',
        })}?invoiceNumExt=${invoiceNumExt}&billingCaRefNo=${payload.caRefNo || caRefNo || ''}`
      )
    );
  }
}

/* EXPORT */
export function* claimStep6Saga() {
  yield takeLatest(createActionType(ClaimStep6StateTypes.GetData, RequestActionTypes.REQUEST), getData);
  yield takeLatest(createActionType(ClaimStep6StateTypes.AssignClaim, RequestActionTypes.REQUEST), assignClaim);
  yield takeLatest(createActionType(ClaimStep6StateTypes.ProcessClaim, RequestActionTypes.REQUEST), processClaim);
  yield takeLatest(createActionType(ClaimStep6StateTypes.SendClaim, RequestActionTypes.REQUEST), sendClaim);
  yield takeLatest(createActionType(ClaimStep6StateTypes.AddNextClaim, RequestActionTypes.REQUEST), addNextClaim);
  yield takeLatest(
    createActionType(ClaimStep6StateTypes.CancelClaimByParent, RequestActionTypes.REQUEST),
    cancelClaimByParent
  );
}
